import { theme } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { useToastContext } from '../../contexts/ToastProvider';
import bookingDocumentsAPI from '../../services/api/booking-documents';
import { BOOKING_ATTACHMENT_TYPES } from '../../shared/constants';
import { formatDate } from '../../shared/helpers/formatDate';
import {
  StyledBookingReceiptDate,
  StyledBookingReceiptDownloadButton,
  StyledBookingReceiptDownloadIcon,
  StyledBookingReceiptWrapper,
  StyledSpinner,
} from './ThankYou.styles';

const BookingReceipt = ({ booking_id }: { booking_id: string }) => {
  const [noResponseError, setNoResponseError] = useState<boolean>(false);
  const [getUploadedFileLoading, setGetUploadedFileLoading] =
    useState<boolean>(false);
  const toast = useToastContext();

  const { mutateAsync, isLoading } = useMutation(
    bookingDocumentsAPI.getBookingReceiptUrl,
    {
      onSuccess: (response: { presigned_url: string }) => {
        window.open(response.presigned_url, '_blank');
      },
      onError: () => {
        toast.error(upperFirst(t('error:error')));
      },
    },
  );

  const getUploadedReceiptAttachmentBeforeDownload = async (times = 1) => {
    setGetUploadedFileLoading(true);
    try {
      const receiptAttachments =
        await bookingDocumentsAPI.getBookingAttachmentByType({
          bookingId: booking_id,
          type: BOOKING_ATTACHMENT_TYPES.BOOKING_RECEIPT,
        });
      if (receiptAttachments?.length > 0) {
        setGetUploadedFileLoading(false);
      } else if (times > 10) {
        setNoResponseError(true);
        setGetUploadedFileLoading(false);
      } else {
        setTimeout(
          getUploadedReceiptAttachmentBeforeDownload.bind(null, times + 1),
          2000,
        );
      }
    } catch (error) {
      setGetUploadedFileLoading(false);
      console.log(
        '[getUploadedReceiptAttachmentBeforeDownload] Error: ',
        error,
      );
    }
  };

  useEffect(() => {
    getUploadedReceiptAttachmentBeforeDownload();
  }, []);

  useEffect(() => {
    if (noResponseError) {
      toast.error(upperFirst(t('error:notPossibleToGetBookingReceipt')));
      setNoResponseError(false);
    }
  }, [noResponseError]);

  const { t } = useTranslation(['booking']);
  return (
    <StyledBookingReceiptWrapper>
      <StyledBookingReceiptDate>
        {formatDate(new Date())}
      </StyledBookingReceiptDate>
      {upperFirst(t('booking:bookingReceipt'))}
      <StyledBookingReceiptDownloadButton
        user="tenant"
        variant="primary"
        onClick={() => mutateAsync(booking_id)}
        disabled={isLoading || getUploadedFileLoading}
      >
        {isLoading || getUploadedFileLoading ? (
          <StyledSpinner foregroundColor={theme.colors.cross.ultralightGray} />
        ) : (
          <StyledBookingReceiptDownloadIcon aria-hidden="true" />
        )}
        {upperFirst(t('booking:downloadDocument'))}
      </StyledBookingReceiptDownloadButton>
    </StyledBookingReceiptWrapper>
  );
};

export default BookingReceipt;
